import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import SaleStatus from "./SaleStatus";
import SaleItem from "./SaleItem";
import DocumentRef from "./DocumentRef";
import CustomerTerms from "./CustomerTerms";
import { ShipTo, BillTo } from "./BillTo";
import Total from "./QuoteTotal";
import { SALES_LEAD_TYPES } from "./SalesLead";
import {
  deepFreeze,
  formatCurrency,
  formatPercentage,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { compact, flatten, sortBy, uniq } from "lodash";
import { LOCATION_INFO } from "../constants";
import { vendorQuoteChecksOn } from "../util/vendorOrderNumber";

export const SALE_FIELDS = {
  status: null,
  type: "",
  leadID: "",
  quoteID: "",
  brandSeries: {},
  assignedTo: [],
  isWholesale: false,
  // INTERNAL ONLY. Carried from the quote: why the Shipping/Delivery Charge
  // was waived or reduced on a job under the threshold, so it can be reviewed
  // on the sale after the fact. Safe to hold on the sale document — customers
  // cannot read it (firestore.rules: sales are `allow read: if isEmployee()`),
  // unlike the quote, whose customer copy is a document they CAN read.
  shippingWaiveReason: "",
  // INTERNAL ONLY. Carried from the quote: why the paid-in-full protocol was
  // overridden on a cash sale under the threshold, so it can be reviewed on
  // the sale after the fact. Same footing as shippingWaiveReason above.
  cashSaleWaiveReason: "",
  location: null,
  commissionSplit: null,
  // Parallel to `assignedTo`/`commissionSplit`: "share" (of the commission
  // pot; shares total 100) or "rate" (a fixed percent of the pre-tax paid off
  // the top, see Commission.isSplitCommission). Null on older sales means
  // every entry is a share.
  commissionSplitTypes: null,
  teamID: null,
  soldDate: null,
  soldBy: null,
  soldTo: null,
  shipTo: null,
  deliveryContact: null,
  plans: [],
  drawings: [],
  intentLetter: null,
  contracts: [],
  legalDocs: [],
  aia: false,
  notax: false,
  notaxCert: null,
  tax: "",
  terms: null,
  total: null,
  downPaymentAmount: 0,
  downPaymentFee: 0,
  holdScreens: true,
  holdHardware: true,
  requiresPunchOut: true,
  // Stamped by scripts/backfill-service-center.mjs on the sales it found
  // still owing a punchout when the Service Center launched: their punchout
  // was never recorded, so the service manager may close it out without a
  // visit. Never set for a sale that enters service after that.
  legacyPunchOut: false,
  locked: false,
  canceled: false,
  canceledReason: null,
  canceledDate: null,
  canceledBy: null,
  additionalCosts: [],

  // Purchaser claiming
  orderingClaimedBy: null,
  orderingClaimedAt: null,

  // Defer Ordering to Purchasers, from the lead's sold outcome: purchasers,
  // not the salesperson, are told the sale is ready to order (touchSale).
  deferOrdering: false,
  // The vendors' quote numbers, `[{ vendor, number }]`, from the sold outcome
  // and editable here. A vendor can have several: a salesperson may enter one
  // job into the vendor's system as several quotes. The Marvin sync places an
  // order whose PO cannot be read by Marvin's numbers, and records in
  // `vendorQuoteChecks` each number one character off Marvin's (see
  // `vendorQuoteIssues`).
  vendorQuotes: [],
  vendorQuoteChecks: [],

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALE_FIELDS);

export default class Sale extends DataStructure {
  constructor(projectID, id, data = {}) {
    data = sanitize(data, SALE_FIELDS);

    // Backward compatibility
    data.soldTo = new BillTo(data.soldTo).data;
    data.shipTo = new ShipTo(data.shipTo).data;
    if (!data.assignedTo || !data.assignedTo.length) {
      const project = store.state.main.projects[projectID];
      data.assignedTo = [project ? project.data.assignedTo : data.soldBy];
      data.commissionSplit = [100];
    }
    if (!data.deliveryContact && data.soldTo.type === "person") {
      data.deliveryContact = data.soldTo.address.contact;
    }

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.soldDate = timestampToDate(data.soldDate);
    data.orderingClaimedAt = timestampToDate(data.orderingClaimedAt);
    data.canceledDate = timestampToDate(data.canceledDate);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.permissions = Object.freeze({
      create: Sale.canBeCreatedBy,
      edit: this.canBeEditedBy,
      change: this.canBeChangedBy,
      cancel: this.canBeCanceledBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      reassign: this.canBeReassignedBy,
      "create-order": this.canCreateOrder,
      "create-ack": this.canCreateAck,
      "create-bill": this.canCreateBill,
      "create-packing-slip": this.canCreatePackingSlip,
      "create-issue": this.canCreateIssue,
      "close-out-punchout": this.canClosePunchoutBy,
    });

    this.data.status = this.status.data;

    // Terms
    this.data.terms = this.terms.data;

    // Total
    if (!this.data.total || typeof this.data.total !== "object") {
      this.data.total = {};
    }
    if (!("taxPercent" in this.data.total)) {
      this.data.total.taxPercent = this.data.tax / 100;
    }
    this.data.total = this.total.data;

    // Documents
    this.data.plans = this.plans.map((d) => d.data);
    this.data.drawings = this.drawings.map((d) => d.data);
    this.data.contracts = this.contracts.map((d) => d.data);
    this.data.legalDocs = this.legalDocs.map((d) => d.data);
    if (this.data.intentLetter) {
      this.data.intentLetter = this.intentLetter.data;
    }
    if (this.data.notaxCert) {
      this.data.notaxCert = this.notaxCert.data;
    }
  }

  // Permissions

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    return user.isAdmin || (!this.data.locked && user.isEmployee);
  }

  canBeChangedBy(user) {
    return !this.data.locked && user.isEmployee;
  }

  canBeCanceledBy(user) {
    if (!user.isEmployee || this.data.canceled || this.data.locked) {
      return false;
    }
    // Standard path: no deliveries or installations have happened yet, so
    // the sale can be cleanly unwound (received material → inventory,
    // sale-balance entry reversed, commissions deleted).
    if (
      this.data.status.delivery.complete === 0 &&
      this.data.status.install.completeItems === 0
    ) {
      return true;
    }
    // Refund path: the sale was fully reversed via credit memos and the
    // customer balance is settled (sale 03900045A repro). Cancellation
    // here is purely paperwork — clears commissions and marks the sale
    // canceled — without touching delivered material or finances.
    const total = this.data.total || {};
    return (
      (total.credit || 0) > 0 &&
      (total.actualPreTax || 0) <= 0 &&
      (total.balance || 0) === 0
    );
  }

  canBeDeletedBy(user) {
    return false;
  }

  canBeUnlockedBy(user) {
    return !this.data.canceled && user.isAdmin;
  }

  canBeReassignedBy(user) {
    return (
      !this.data.locked &&
      (user.isSuper ||
        this.data.assignedTo.includes(user.id) ||
        this.isTeamMember(user.id))
    );
  }

  // Check if a user is on the same sales team as any assigned user
  isTeamMember(userID) {
    const salesTeams = store.state.main.salesTeams || {};
    for (const team of Object.values(salesTeams)) {
      if (!team.data.active) continue;
      const memberIDs = team.memberIDs || [];
      // Check if both the user and any assignee are on this team
      if (memberIDs.includes(userID)) {
        const hasAssignedTeammate = this.data.assignedTo.some((assigneeID) =>
          memberIDs.includes(assigneeID)
        );
        if (hasAssignedTeammate) return true;
      }
    }
    return false;
  }

  canCreateOrder(user) {
    if (!this.id || this.data.locked || !user.isEmployee) return false;
    if (this.data.orderingClaimedBy) {
      return this.data.orderingClaimedBy === user.id;
    }
    return true;
  }

  canCreateAck(user) {
    return this.id && !this.data.locked && user.isEmployee;
  }

  canCreateBill(user) {
    return (
      this.id && !this.data.locked && !this.data.canceled && user.isEmployee
    );
  }

  canCreatePackingSlip(user) {
    return this.id && !this.data.locked && user.isEmployee;
  }

  canCreateIssue(user) {
    return this.id && !this.data.locked && user.isEmployee;
  }

  /**
   * Justin (2026-09-15): the service manager closes out punchouts that were
   * done before the Service Center existed. Service managers and admins only,
   * and only for a legacy sale (see needsLegacyPunchoutCloseOut) — the lock
   * does not matter because the write happens server-side.
   */
  canClosePunchoutBy(user) {
    return (
      this.needsLegacyPunchoutCloseOut &&
      (user.isAdmin || (user.isEmployee && user.hasRole("service")))
    );
  }

  /**
   * Flagged by the backfill as owing a punchout when the Service Center
   * launched, and still sitting in service waiting on it. Age is no test
   * (Justin: a house can take three years, so old screens in stock can be
   * genuinely active); the flag is. Anything that enters service later takes
   * the normal route — a scheduled visit, completed by the tech.
   */
  get needsLegacyPunchoutCloseOut() {
    const data = this.data;
    const status = data.status || {};
    return Boolean(
      this.id &&
        data.legacyPunchOut === true &&
        !data.canceled &&
        data.requiresPunchOut === true &&
        data.total &&
        data.total.items > 0 &&
        status.punchOutComplete !== true &&
        status.type === "service"
    );
  }

  // Database

  get path() {
    return Sale.getPath(this.projectID, this.id);
  }

  get stateID() {
    return this.projectID + this.id;
  }

  static getStateID(path) {
    const match = path.match(/^projects\/([^/]+)\/sales\/([^/]+)$/);
    if (match) {
      return match[1] + match[2];
    }
    return null;
  }

  static getPath(projectID, id) {
    let path = `projects/${projectID}/sales`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(sale) {
    return sale.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Sale(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Formatting

  get location() {
    return this.data.location ? LOCATION_INFO[this.data.location] : null;
  }

  get project() {
    return this.projectID
      ? store.state.main.projects[this.projectID] || null
      : null;
  }

  get orders() {
    return sortBy(
      Object.values(store.state.main.orders).filter(
        (order) =>
          order.projectID === this.projectID && order.saleID === this.id
      ),
      "created"
    );
  }

  get items() {
    const items = Object.values(store.state.main.saleItems).filter(
      (item) => item.projectID === this.projectID && item.saleID === this.id
    );
    return SaleItem.sortByLineAndAccessory(items);
  }

  get labors() {
    return sortBy(
      Object.values(store.state.main.saleLabors).filter(
        (labor) =>
          labor.projectID === this.projectID && labor.saleID === this.id
      ),
      "line"
    );
  }

  get miscItems() {
    return sortBy(
      Object.values(store.state.main.miscItems).filter(
        (item) => item.projectID === this.projectID && item.saleID === this.id
      ),
      "line"
    );
  }

  get issues() {
    return sortBy(
      Object.values(store.state.main.issues).filter(
        (issue) =>
          issue.projectID === this.projectID && issue.saleID === this.id
      ),
      "created"
    ).reverse();
  }

  get deliveries() {
    return sortBy(
      this.data.status.delivery.events.map((id) => store.state.main.events[id]),
      "created"
    ).reverse();
  }

  get installations() {
    return sortBy(
      this.data.status.install.events.map((id) => store.state.main.events[id]),
      "created"
    ).reverse();
  }

  get type() {
    return SALES_LEAD_TYPES[this.data.type];
  }

  get status() {
    return new SaleStatus(this, this.data.status);
  }

  get lead() {
    return this.data.leadID
      ? store.state.main.salesLeads[this.data.leadID]
      : null;
  }

  get quote() {
    return this.data.quoteID
      ? store.state.main.quotes[this.data.quoteID]
      : null;
  }

  get brands() {
    return Object.keys(this.data.brandSeries).sort();
  }

  get series() {
    return uniq(flatten(Object.values(this.data.brandSeries))).sort();
  }

  get shipTo() {
    return new ShipTo(this.data.shipTo);
  }

  get isPickupSale() {
    return Boolean(this.data.shipTo?.pickup);
  }

  getQuantityAtPickupLocation(item) {
    if (!item || !this.isPickupSale) return 0;

    const pickupAddress = this.shipTo.address;
    if (!pickupAddress) return 0;

    return item.itemLocation.reduce((total, location) => {
      if (!location?.isTemporary || !location.address) {
        return total;
      }
      return location.address.isSameLocation(pickupAddress)
        ? total + (location.data.quantity || 0)
        : total;
    }, 0);
  }

  hasUnscheduledDelivery(item) {
    if (!item) return false;

    const quantity = item.data.quantity || 0;
    const quantityReady = item.data.quantityReady || 0;
    const quantityScheduled = item.data.quantityScheduled || 0;
    const quantityDelivered = item.data.quantityDelivered || 0;

    if (
      !quantity ||
      quantityDelivered >= quantity ||
      quantityReady <= quantityScheduled
    ) {
      return false;
    }

    let unscheduledQty = quantityReady - quantityScheduled;
    if (this.isPickupSale) {
      unscheduledQty -= this.getQuantityAtPickupLocation(item);
    }

    return unscheduledQty > 0;
  }

  hasUnscheduledPencilDelivery(item) {
    if (!item) return false;

    const quantity = item.data.quantity || 0;
    const quantityDelivered = item.data.quantityDelivered || 0;
    const quantityScheduled = item.data.quantityScheduled || 0;

    if (!quantity || quantityDelivered >= quantity) {
      return false;
    }

    let remainingQty = quantity - quantityDelivered;
    if (this.isPickupSale) {
      remainingQty -= this.getQuantityAtPickupLocation(item);
    }

    return remainingQty > 0 && quantityScheduled < remainingQty;
  }

  canScheduleInstallationLabor(labor) {
    if (!labor) return false;

    const isFullyCompleted = labor.data.status.fullyCompleted;
    const quantityReady = labor.data.status.quantityReady || 0;
    const quantity = labor.data.quantity || 0;
    const hoursScheduled = labor.data.status.hoursScheduled || 0;
    const manHours = labor.data.manHours || 0;

    if (isFullyCompleted || manHours <= hoursScheduled) {
      return false;
    }

    return quantityReady > 0 || !quantity;
  }

  canPencilScheduleInstallationLabor(labor) {
    if (!labor) return false;

    const isFullyCompleted = labor.data.status.fullyCompleted;
    const hoursScheduled = labor.data.status.hoursScheduled || 0;
    const manHours = labor.data.manHours || 0;

    if (isFullyCompleted || manHours <= hoursScheduled) {
      return false;
    }

    return true;
  }

  /**
   * True when every unit is delivered and the ONLY delivery work left is
   * screens/hardware the customer asked us to HOLD in the warehouse.
   *
   * DeliveriesToSchedule hides these to reduce clutter — held misc sits with
   * us for months until the customer calls for it, so it is not actionable
   * scheduling work. That filter used to key on the `schedulingMisc` status,
   * but computeSaleStatus stopped producing it (e9b760dfd), so the filter
   * silently stopped matching and the sales reappeared in the widget — which
   * is why they now look like they stay there after being scheduled.
   *
   * Expressed from the counters and hold flags directly so it cannot rot
   * against a status string again.
   */
  get hasOnlyHeldMiscToSchedule() {
    const delivery = this.data.status.delivery || {};
    const totalItems = this.data.total.items || 0;
    const totalScreens = this.data.total.screens || 0;
    const totalHardware = this.data.total.hardware || 0;

    // Every unit has to be delivered first; otherwise there is real work.
    if (totalItems && (delivery.complete || 0) < totalItems) return false;

    const screensOutstanding =
      totalScreens > 0 && (delivery.screensScheduled || 0) < totalScreens;
    const hardwareOutstanding =
      totalHardware > 0 && (delivery.hardwareScheduled || 0) < totalHardware;

    // Nothing misc outstanding: not this case at all.
    if (!screensOutstanding && !hardwareOutstanding) return false;

    // Only clutter if everything still outstanding is HELD. An unheld screen
    // left to schedule is genuine work and must stay visible.
    return (
      (!screensOutstanding || Boolean(this.data.holdScreens)) &&
      (!hardwareOutstanding || Boolean(this.data.holdHardware))
    );
  }

  get hasDeliveryToSchedule() {
    const delivery = this.data.status.delivery || {};
    const totalItems = this.data.total.items || 0;
    const totalScreens = this.data.total.screens || 0;
    const totalHardware = this.data.total.hardware || 0;
    const ready = delivery.ready || 0;
    const scheduled = delivery.scheduled || 0;
    const complete = delivery.complete || 0;
    const screensScheduled = delivery.screensScheduled || 0;
    const hardwareScheduled = delivery.hardwareScheduled || 0;

    // CPU (customer-pickup) sales: the pickup normally IS the delivery, so a
    // delivery scheduling action is only relevant when ready stock is NOT yet
    // at the pickup location and must be transferred there first (a "delivery
    // to the pickup location", forced to a temporary destination in EventEdit).
    // Once stock reaches the pickup location `status.pickup.*Ready` catches up
    // to `status.delivery.*Ready` — via the QC-at-warehouse mirror
    // (UPDATE_SALE_ITEM_STATUS) or a completed delivery-to-pickup
    // (buildDeliveryCompletionChanges) — closing the gap so the action hides.
    // The `scheduled - complete` term excludes stock already en route on a
    // booked-but-incomplete delivery so the button doesn't re-appear after a
    // delivery-to-pickup has been scheduled.
    if (this.isPickupSale) {
      const pickup = this.data.status.pickup || {};
      const awayFromPickup = (
        dimReady,
        dimScheduled,
        dimComplete,
        pickupReady,
        pickupComplete
      ) =>
        dimReady -
        (pickupReady || 0) -
        (pickupComplete || 0) -
        Math.max(0, (dimScheduled || 0) - (dimComplete || 0));

      const unitsAway = awayFromPickup(
        ready,
        scheduled,
        complete,
        pickup.ready,
        pickup.complete
      );
      const screensAway = awayFromPickup(
        delivery.screensReady || 0,
        screensScheduled,
        delivery.screensComplete || 0,
        pickup.screensReady,
        pickup.screensComplete
      );
      const hardwareAway = awayFromPickup(
        delivery.hardwareReady || 0,
        hardwareScheduled,
        delivery.hardwareComplete || 0,
        pickup.hardwareReady,
        pickup.hardwareComplete
      );

      return unitsAway > 0 || screensAway > 0 || hardwareAway > 0;
    }

    const hasScreensToSchedule =
      totalScreens > 0 && screensScheduled < totalScreens;
    const hasHardwareToSchedule =
      totalHardware > 0 && hardwareScheduled < totalHardware;
    // Child accessories are separate stock excluded from `total.items`, so a
    // ready-but-unscheduled accessory has to keep the sale in the widget on
    // its own — otherwise it is stranded in the warehouse with no action
    // pointing at it once every unit has been delivered (sale 01000033A).
    const totalAccessories = this.data.total.accessories || 0;
    const accessoriesReady = delivery.accessoriesReady || 0;
    const accessoriesScheduled = delivery.accessoriesScheduled || 0;
    const accessoriesComplete = delivery.accessoriesComplete || 0;
    const hasAccessoriesToSchedule =
      totalAccessories > 0 &&
      accessoriesComplete < totalAccessories &&
      accessoriesReady > accessoriesScheduled;

    if (
      (!totalItems || complete >= totalItems) &&
      !hasScreensToSchedule &&
      !hasHardwareToSchedule &&
      !hasAccessoriesToSchedule
    ) {
      return false;
    }

    return (
      ready > scheduled ||
      hasScreensToSchedule ||
      hasHardwareToSchedule ||
      hasAccessoriesToSchedule
    );
  }

  get hasPickupToSchedule() {
    // Only customer-pickup (CPU) sales expose a pickup scheduling action.
    // `status.pickup.ready` is kept in sync with deliveries-to-pickup AND
    // QC-direct-into-the-pickup-warehouse (see UPDATE_SALE_ITEM_STATUS and
    // its backend mirrors). Any pickup sale with ready items not yet
    // scheduled for pickup is eligible to schedule one.
    if (!this.isPickupSale) return false;

    const pickup = this.data.status.pickup || {};
    const totalItems = this.data.total.items || 0;
    const totalScreens = this.data.total.screens || 0;
    const totalHardware = this.data.total.hardware || 0;
    const ready = pickup.ready || 0;
    const scheduled = pickup.scheduled || 0;
    const complete = pickup.complete || 0;
    const screensScheduled = pickup.screensScheduled || 0;
    const hardwareScheduled = pickup.hardwareScheduled || 0;

    const hasScreensToSchedule =
      totalScreens > 0 && screensScheduled < totalScreens;
    const hasHardwareToSchedule =
      totalHardware > 0 && hardwareScheduled < totalHardware;

    // Once everything has been picked up there is nothing left to schedule.
    // Mirrors the `complete >= totalItems` guard in `hasDeliveryToSchedule`;
    // without it, an inflated `pickup.ready` counter (e.g. an item delivered
    // from multiple bins, as on sale 04000015A) keeps the CPU action lit even
    // after the pickup has been scheduled and completed.
    if (
      (!totalItems || complete >= totalItems) &&
      !hasScreensToSchedule &&
      !hasHardwareToSchedule
    ) {
      return false;
    }

    return ready > scheduled || hasScreensToSchedule || hasHardwareToSchedule;
  }

  get hasInstallationToSchedule() {
    const install = this.data.status.install || {};
    const totalHours = install.totalHours || 0;
    const scheduledHours = install.scheduledHours || 0;
    const totalLabors = install.totalLabors || 0;
    const completeLabors = install.completeLabors || 0;
    const totalItems = install.totalItems || 0;
    const readyItems = install.readyItems || 0;

    if (!totalHours || scheduledHours >= totalHours) {
      return false;
    }

    if (totalLabors && completeLabors >= totalLabors) {
      return false;
    }

    return readyItems > 0 || !totalItems;
  }

  get hasDeliveryToPencilSchedule() {
    // CPU sales never pencil-schedule a delivery — see `hasDeliveryToSchedule`.
    if (this.isPickupSale) return false;

    const delivery = this.data.status.delivery || {};
    const totalItems = this.data.total.items || 0;
    const totalScreens = this.data.total.screens || 0;
    const totalHardware = this.data.total.hardware || 0;
    const scheduled = delivery.scheduled || 0;
    const complete = delivery.complete || 0;
    const screensScheduled = delivery.screensScheduled || 0;
    const hardwareScheduled = delivery.hardwareScheduled || 0;

    const hasItemsToPencilSchedule =
      totalItems > 0 && complete < totalItems && scheduled < totalItems;
    const hasScreensToPencilSchedule =
      totalScreens > 0 && screensScheduled < totalScreens;
    const hasHardwareToPencilSchedule =
      totalHardware > 0 && hardwareScheduled < totalHardware;
    const totalAccessories = this.data.total.accessories || 0;
    const hasAccessoriesToPencilSchedule =
      totalAccessories > 0 &&
      (delivery.accessoriesComplete || 0) < totalAccessories &&
      (delivery.accessoriesScheduled || 0) < totalAccessories;

    return (
      hasItemsToPencilSchedule ||
      hasScreensToPencilSchedule ||
      hasHardwareToPencilSchedule ||
      hasAccessoriesToPencilSchedule
    );
  }

  get hasInstallationToPencilSchedule() {
    const install = this.data.status.install || {};
    const totalHours = install.totalHours || 0;
    const scheduledHours = install.scheduledHours || 0;
    const totalLabors = install.totalLabors || 0;
    const completeLabors = install.completeLabors || 0;

    if (!totalHours || scheduledHours >= totalHours) {
      return false;
    }

    if (totalLabors && completeLabors >= totalLabors) {
      return false;
    }

    return true;
  }

  get hasPencilSchedulingActions() {
    return (
      this.hasDeliveryToPencilSchedule || this.hasInstallationToPencilSchedule
    );
  }

  get assignedTo() {
    return this.data.assignedTo
      .map((id) => store.state.main.users[id])
      .filter(Boolean);
  }

  get isWholesale() {
    return Boolean(this.data.isWholesale);
  }

  // Whose sales totals this sale counts toward, and by how much: `{ userID:
  // fraction }`, fractions summing to 1. Mirror of `salesCredit` in
  // functions/commissionSplits.js -- keep the two identical so the live
  // pages (reports, goals, games) agree with the aggregates.
  //
  // Justin (2026-09-10): "just leave it under primary persons goals and
  // totals to avoid mathing all the time" -- a split moves commission, not
  // revenue: share presets and fixed-rate splits leave the whole sale with
  // the primary salesperson, the first (largest) share-holder. A Sales Team
  // sale still divides equally among the team's share-holders; that is the
  // team feature, with its own goals. Fixed-rate recipients never take
  // revenue credit.
  static salesCredit(data) {
    const assignedTo = (data && data.assignedTo) || [];
    const types = Array.isArray(data && data.commissionSplitTypes)
      ? data.commissionSplitTypes
      : [];
    const holders = assignedTo.filter(
      (id, i) => Boolean(id) && types[i] !== "rate"
    );
    if (!holders.length) {
      const fallback = assignedTo.find(Boolean) || (data && data.soldBy);
      return fallback ? { [fallback]: 1 } : {};
    }
    if (data.teamID) {
      return holders.reduce(
        (credit, id) => ({ ...credit, [id]: 1 / holders.length }),
        {}
      );
    }
    return { [holders[0]]: 1 };
  }

  get salesCredit() {
    return Sale.salesCredit(this.data);
  }

  get soldTo() {
    return new BillTo(this.data.soldTo);
  }

  get soldBy() {
    return this.data.soldBy
      ? store.state.main.users[this.data.soldBy] || null
      : null;
  }

  get team() {
    return this.data.teamID
      ? (store.state.main.salesTeams || {})[this.data.teamID] || null
      : null;
  }

  get deliveryContact() {
    return this.data.deliveryContact
      ? store.state.main.contacts[this.data.deliveryContact] || null
      : null;
  }

  get terms() {
    return new CustomerTerms(this.data.terms);
  }

  get total() {
    return new Total(this.data.total);
  }

  // The number of deliverable item units shown on the Items tab (badge and
  // progress denominators). This is the SINGLE definition of "how many items
  // does this sale have" for display, so the tab badge and the
  // `SaleItemProgress` "Full Units" row can never disagree.
  //
  // It mirrors the canonical `classifyItemBucket` rule (un-numbered
  // Screens/Hardware live in `total.screens`/`total.hardware`). Non-held
  // Screens/Hardware are folded into the count because they are delivered as
  // part of the same "Full Units" flow; held ones are tracked separately.
  // Child accessories (`total.accessories`) are also folded in: they are
  // separately received and delivered stock, so leaving them out made the tab
  // badge disagree with the item list and hid a stranded accessory
  // (sale 01000033A). They have no hold flag, so they always count.
  get displayItemCount() {
    const total = this.data.total || {};
    let count = total.items || 0;
    if (!this.data.holdHardware) count += total.hardware || 0;
    if (!this.data.holdScreens) count += total.screens || 0;
    count += total.accessories || 0;
    return count;
  }

  get vendors() {
    const vendorStatuses = this.data.status.vendors || {};
    const statusVendors = Object.keys(vendorStatuses).filter((vendor) => {
      const status = vendorStatuses[vendor];
      return (
        status &&
        typeof status === "object" &&
        Object.prototype.hasOwnProperty.call(status, "fullyOrdered")
      );
    });

    // Other Charges (misc items) carry the vendor in `brand` — the
    // InvoiceEdit dialog labels that field "Vendor". Without including
    // those, a Material-Only sale whose only vendor exposure is an Other
    // Charge has an empty `vendors` array, hiding the Vendor tab and
    // leaving nowhere to attach vendor documentation.
    const miscVendors = this.miscItems.map((m) => m.data?.brand);

    return uniq(
      statusVendors
        .concat(this.orders.map((o) => o.data.vendor))
        .concat(miscVendors)
    )
      .filter(Boolean)
      .sort();
  }

  get requiresDownPayment() {
    return this.data.terms.cashSale && this.data.total.price > 0;
  }

  get downPaymentDue() {
    return this.data.terms.cashSale
      ? Math.max(
          0,
          roundPrice(
            (this.data.total.price * this.data.terms.downPayment) / 100 -
              this.data.downPaymentAmount
          )
        )
      : 0;
  }

  get downPaymentDueFormatted() {
    return formatCurrency(this.downPaymentDue);
  }

  // Sum of the sale's labor line prices, maintained on `total.labor` by
  // QuoteTotal.computeTotal so it is available without loading the labor
  // subcollection. For cash sales with installation, labor is billed/collected
  // separately after the labor is complete, so it is excluded from the
  // material balance that must be collected before delivery.
  get laborPrice() {
    return roundPrice(this.data.total.labor || 0);
  }

  // Outstanding balance attributable to material on a cash sale. The remaining
  // balance covers both material and (for installed sales) labor; labor is
  // collected later, so the material amount still due before delivery is the
  // balance less the labor portion.
  get materialAmountDue() {
    if (!this.data.terms.cashSale) {
      return 0;
    }
    return Math.max(
      0,
      roundPrice((this.data.total.balance || 0) - this.laborPrice)
    );
  }

  get materialAmountDueFormatted() {
    return formatCurrency(this.materialAmountDue);
  }

  get hasUnpaidMaterial() {
    return this.materialAmountDue > 0;
  }

  // Total outstanding balance still owed on the sale (material + labor).
  get openBalance() {
    return Math.max(0, roundPrice(this.data.total.balance || 0));
  }

  get openBalanceFormatted() {
    return formatCurrency(this.openBalance);
  }

  // A cash sale that has been put on the schedule (delivery or install) and is
  // being worked through delivery/installation. Used by the
  // ScheduledUnpaidCashSales widget to surface scheduled cash sales that still
  // owe a balance, without blocking scheduling.
  get isScheduledForDeliveryOrInstall() {
    return ["scheduled", "pickup", "install", "installing"].includes(
      this.data.status.type
    );
  }

  get isScheduledCashSaleWithBalance() {
    return Boolean(
      this.data.terms.cashSale &&
        this.openBalance > 0 &&
        !this.data.canceled &&
        this.isScheduledForDeliveryOrInstall
    );
  }

  get plans() {
    return this.data.plans.map((d) => new DocumentRef(d));
  }

  get drawings() {
    return this.data.drawings.map((d) => new DocumentRef(d));
  }

  get contracts() {
    return this.data.contracts.map((d) => new DocumentRef(d));
  }

  get legalDocs() {
    return this.data.legalDocs.map((d) => new DocumentRef(d));
  }

  get intentLetter() {
    return this.data.intentLetter
      ? new DocumentRef(this.data.intentLetter)
      : null;
  }

  get notaxCert() {
    return this.data.notaxCert ? new DocumentRef(this.data.notaxCert) : null;
  }

  get tax() {
    return formatPercentage(this.data.tax);
  }

  get orderNotes() {
    return compact(Object.values(this.data.status.orders).map((o) => o.notes));
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  get orderingClaimedBy() {
    return this.data.orderingClaimedBy
      ? store.state.main.users[this.data.orderingClaimedBy] || null
      : null;
  }

  /**
   * The vendors a quote number can be recorded under: every vendor on the
   * sale but Labor, and any vendor one is already recorded under.
   */
  get quoteVendors() {
    return uniq(
      this.vendors
        .filter((vendor) => vendor !== "Labor")
        .concat(this.data.vendorQuotes.map((quote) => quote.vendor))
    );
  }

  /**
   * The vendor sync's open questions about the sale's vendor quote numbers,
   * one per number: Marvin ships an order from a quote one character off it,
   * and that order was not placed on the sale (functions/vendorShippingSync.js).
   * A flag drops out once its number is off the sale, since it then describes
   * a number the sale no longer has, until the next sync clears it.
   */
  get vendorQuoteIssues() {
    return vendorQuoteChecksOn(
      this.data.vendorQuoteChecks,
      this.data.vendorQuotes
    ).filter((check) => check.status === "near-match");
  }

  toString() {
    return this.stateID;
  }
}
